interface GaugeProps {
    // undefined while the value is withheld: the needle rests at zero.
    value?: number;
    // The end of the scale, divided into five equal bands.
    top: number;
    // The text under the needle.
    valueText: string;
    label: string;
}

const BANDS = 5;
const CENTER = { x: 100, y: 100 };
const RADIUS = 72;
const LABEL_RADIUS = 89;
const NEEDLE_LENGTH = 58;
const BAND_GAP = 0.004;

// The needle's rotation in degrees from pointing straight up: -90 at zero, 90 at
// the end of the scale. A value past the end stops there.
export const needleAngle = (value: number | undefined, top: number): number =>
    (Math.min(Math.max(value ?? 0, 0), top) / Math.max(top, 1)) * 180 - 90;

// A point on the half circle: fraction 0 is at the left end, 1 at the right.
const polar = (fraction: number, radius: number) => {
    const angle = Math.PI * (1 - fraction);
    return { x: CENTER.x + radius * Math.cos(angle), y: CENTER.y - radius * Math.sin(angle) };
};

const arc = (from: number, to: number) => {
    const start = polar(from, RADIUS);
    const end = polar(to, RADIUS);
    return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${RADIUS} ${RADIUS} 0 0 1 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
};

// A plain SVG half-circle gauge with five coloured bands and a needle.
export function Gauge({ value, top, valueText, label }: GaugeProps) {
    const bands = Array.from({ length: BANDS }, (_, index) => index);
    const ticks = Array.from({ length: BANDS + 1 }, (_, index) => index);

    return (
        <svg className="gauge" viewBox="0 0 200 136" role="img" aria-label={label}>
            {bands.map(band => (
                <path
                    key={band}
                    className={`gauge-band gauge-band-${band + 1}`}
                    d={arc(band / BANDS + (band === 0 ? 0 : BAND_GAP), (band + 1) / BANDS - (band === BANDS - 1 ? 0 : BAND_GAP))}
                />
            ))}
            {ticks.map(tick => {
                const position = polar(tick / BANDS, LABEL_RADIUS);
                return (
                    <text key={tick} x={position.x} y={position.y + 3} textAnchor="middle" className="chart-text">
                        {(top / BANDS) * tick}
                    </text>
                );
            })}
            <g
                className="gauge-needle"
                data-testid="gauge-needle"
                style={{ transform: `rotate(${needleAngle(value, top)}deg)`, transformOrigin: `${CENTER.x}px ${CENTER.y}px` }}
            >
                <polygon points={`${CENTER.x - 4},${CENTER.y} ${CENTER.x},${CENTER.y - NEEDLE_LENGTH} ${CENTER.x + 4},${CENTER.y}`} />
                <circle cx={CENTER.x} cy={CENTER.y} r={6} />
            </g>
            <text x={CENTER.x} y={129} textAnchor="middle" className="gauge-value" data-testid="gauge-value">{valueText}</text>
        </svg>
    );
}
